import { type Assessment } from "@core/assessments/types";
import { assessmentServerTimestamp } from "@core/assessments/utils/assessment-lifecycle";
import type { Time } from "@core/shared/types";
import { trans } from "@mongez/localization";

/**
 * The "now" used for upcoming/expired status. Prefers the server clock sent
 * with the API response (same value during SSR and hydration, and immune to a
 * wrong device clock), falling back to the client clock.
 */
export function getAssessmentCardNow(assessment: Assessment): number {
  return (
    assessmentServerTimestamp(assessment.serverTime) ??
    assessmentServerTimestamp(assessment.studentQuiz?.serverTime) ??
    Date.now()
  );
}

export function useAssessmentCard(assessment: Assessment) {
  const now = getAssessmentCardNow(assessment);

  // Helper function to check if a date is valid and in the future
  const isDateInFuture = (date?: Time) => {
    return !!date && Number.isFinite(date.timestamp) && date.timestamp > now;
  };

  // Helper function to check if a date is valid and in the past
  const isDateInPast = (date?: Time) => {
    return !!date && Number.isFinite(date.timestamp) && date.timestamp < now;
  };

  // Check if assessment has studentQuiz
  const hasStudentQuiz = !!assessment.studentQuiz;

  // Assessment is upcoming if it has a start date and it's in the future
  const isUpcoming = isDateInFuture(assessment.startsAt);

  // Assessment is expired if it has an end date and it's in the past
  const isExpired = isDateInPast(assessment.endsAt);

  // Assessment is active if:
  // 1. Has studentQuiz and status is not "notStarted" - use student quiz status
  // 2. No dates specified (always available)
  // 3. Only start date and it's in the past or present
  // 4. Only end date and it's in the future or present
  // 5. Both dates and current time is between them
  const isActive = (() => {
    // If has student quiz, determine status based on student quiz
    if (hasStudentQuiz) {
      const studentQuiz = assessment.studentQuiz!;

      // If student quiz is active, the assessment is active
      if (studentQuiz.status === "active") {
        return true;
      }

      // If student quiz is passed, failed, in review, or not started, assessment is not active
      if (
        [
          "passed",
          "failed",
          "inReview",
          "submitted",
          "expired",
          "notStarted",
        ].includes(studentQuiz.status)
      ) {
        return false;
      }
    }

    const hasStartDate = !!assessment.startsAt;
    const hasEndDate = !!assessment.endsAt;

    // No dates specified - always active
    if (!hasStartDate && !hasEndDate) {
      return true;
    }

    // Only start date - active if start date is in the past or present
    if (hasStartDate && !hasEndDate) {
      return !isDateInFuture(assessment.startsAt);
    }

    // Only end date - active if end date is in the future or present
    if (!hasStartDate && hasEndDate) {
      return !isDateInPast(assessment.endsAt);
    }

    // Both dates - active if current time is between start and end
    if (hasStartDate && hasEndDate) {
      return (
        !isDateInFuture(assessment.startsAt) && !isDateInPast(assessment.endsAt)
      );
    }

    return false;
  })();

  const getStatusInfo = () => {
    // If has student quiz, use student quiz status
    if (hasStudentQuiz) {
      const studentQuiz = assessment.studentQuiz!;

      switch (studentQuiz.status) {
        case "notStarted":
          return {
            label: trans("assessments.notStarted"),
            color:
              "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900",
            icon: "Play",
            bgGradient:
              "from-blue-50/60 to-white dark:from-blue-950/30 dark:to-gray-950",
          };
        case "active":
          return {
            label: trans("assessments.active"),
            color:
              "bg-green-100 text-green-800 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-900",
            icon: "Play",
            bgGradient:
              "from-green-50/60 to-white dark:from-green-950/30 dark:to-gray-950",
          };
        case "passed":
          return {
            label: trans("assessments.passed"),
            color:
              "bg-green-100 text-green-800 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-900",
            icon: "CheckCircle",
            bgGradient:
              "from-green-50/60 to-white dark:from-green-950/30 dark:to-gray-950",
          };
        case "failed":
          return {
            label: trans("assessments.failed"),
            color:
              "bg-red-100 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900",
            icon: "XCircle",
            bgGradient:
              "from-red-50/60 to-white dark:from-red-950/30 dark:to-gray-950",
          };
        case "expired":
          return {
            label: trans("assessments.timedOut"),
            color:
              "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900",
            icon: "Calendar",
            bgGradient:
              "from-amber-50/60 to-white dark:from-amber-950/30 dark:to-gray-950",
          };
        case "submitted":
        case "inReview":
          return {
            label: trans("assessments.inReview"),
            color:
              "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-900",
            icon: "Eye",
            bgGradient:
              "from-yellow-50/60 to-white dark:from-yellow-950/30 dark:to-gray-950",
          };
      }
    }

    if (isUpcoming) {
      return {
        label: trans("assessments.comingSoon"),
        color:
          "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900",
        icon: "Calendar",
        bgGradient:
          "from-blue-50/60 to-white dark:from-blue-950/30 dark:to-gray-950",
      };
    }
    if (isActive) {
      return {
        label: trans("assessments.active"),
        color:
          "bg-green-100 text-green-800 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-900",
        icon: "Play",
        bgGradient:
          "from-green-50/60 to-white dark:from-green-950/30 dark:to-gray-950",
      };
    }
    if (isExpired) {
      return {
        label: trans("assessments.expired"),
        color:
          "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600",
        icon: "Calendar",
        bgGradient:
          "from-gray-50/60 to-white dark:from-gray-800/60 dark:to-gray-950",
      };
    }
    return {
      label: trans("assessments.available"),
      color: "bg-primary-100 text-primary-800 border-primary-200",
      icon: "HelpCircle",
      bgGradient:
        "from-primary-50/60 to-white dark:from-primary-950/30 dark:to-gray-950",
    };
  };

  return {
    isUpcoming,
    isActive,
    isExpired,
    getStatusInfo,
    hasStudentQuiz,
  };
}
